/**
 * @file experienceDetailsContent.component.tsx
 * @path apps/website/src/features/experiences/components/experienceDetailsContent.component.tsx
 * @description UI component
 */

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Card,
} from "@sirbastians/ui";
import {
  CarFront,
  CheckCircle,
  ExclamationTriangle,
  ListCheck,
  People,
  PeopleFill,
  SignpostSplit,
  StarFill,
  XCircle,
} from "react-bootstrap-icons";

import { ExperienceDetail, MetadataItem } from "@/features";
import { SectionHeading } from "@/components";

export const ContentSection = ({ data }: { data: ExperienceDetail }) => (
  <div className="lg:col-span-2 space-y-12">
    {/* Metadata pills */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6 border-y border-border py-8">
      {data.metadata.map((meta, index) => (
        <MetadataItem key={index} {...meta} />
      ))}
      {data.difficulty && (
        <MetadataItem
          icon={StarFill}
          label="Difficulty"
          value={data.difficulty}
        />
      )}
      {data.capacity && (
        <MetadataItem
          icon={PeopleFill}
          label="Capacity"
          value={data.capacity}
        />
      )}
    </div>

    {/* Description */}
    <div className="font-open-sans text-muted-foreground text-base leading-relaxed space-y-8">
      <div className="whitespace-pre-line text-foreground/80">
        {data.description}
      </div>

      {/* Highlights */}
      {data.highlights && data.highlights.length > 0 && (
        <div>
          <SectionHeading>Highlights</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.highlights.map((h, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <StarFill className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                <span className="text-foreground/80">{h}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inclusions / Exclusions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8">
        {data.inclusions.length > 0 && (
          <div>
            <SectionHeading>Included</SectionHeading>
            <ul className="space-y-3">
              {data.inclusions.map((inc: string, i: number) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                  <span>{inc}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.exclusions.length > 0 && (
          <div>
            <SectionHeading>Excluded</SectionHeading>
            <ul className="space-y-3">
              {data.exclusions.map((exc: string, i: number) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <XCircle className="h-5 w-5 text-destructive shrink-0" />
                  <span>{exc}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Audience / Great For */}
      {data.audience && data.audience.length > 0 && (
        <div>
          <SectionHeading>Great For</SectionHeading>
          <div className="flex flex-wrap gap-2">
            {data.audience.map((a) => (
              <span
                key={a}
                className="px-4 py-2 rounded-full text-xs font-montserrat font-bold uppercase tracking-widest bg-accent/10 text-accent border border-accent/20"
              >
                {a}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* What to Bring */}
      {data.whatToBring && data.whatToBring.length > 0 && (
        <div>
          <SectionHeading>What to Bring</SectionHeading>
          <div className="grid grid-cols-2 gap-3">
            {data.whatToBring.map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <ListCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-foreground/80">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pricing Options */}
      {data.pricingOptions && data.pricingOptions.length > 0 && (
        <div>
          <SectionHeading>Pricing Options</SectionHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.pricingOptions.map((opt, i) => (
              <Card
                key={i}
                className="bg-card/40 backdrop-blur-md border border-border neuro-well rounded-2xl p-5"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-montserrat font-bold text-sm text-foreground">
                      {opt.label}
                    </p>
                    {opt.description && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {opt.description}
                      </p>
                    )}
                  </div>
                  <span className="font-bold text-lg text-accent">
                    {opt.price}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Itinerary */}
      {data.itinerary && data.itinerary.length > 0 && (
        <div>
          <SectionHeading>Itinerary</SectionHeading>
          <div className="relative pl-8 space-y-6 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-px before:bg-border">
            {data.itinerary.map((step, i) => (
              <div key={i} className="relative">
                <div className="absolute -left-8 top-1 h-6 w-6 rounded-full bg-accent/10 border-2 border-accent flex items-center justify-center">
                  <span className="text-[9px] font-bold text-accent">
                    {i + 1}
                  </span>
                </div>
                <div>
                  {step.time && (
                    <span className="text-[10px] font-montserrat font-black uppercase tracking-[0.3em] text-accent mb-1 block">
                      {step.time}
                    </span>
                  )}
                  <p className="font-montserrat font-bold text-sm text-foreground">
                    {step.title}
                  </p>
                  {step.description && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {step.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Routes */}
      {data.routes && data.routes.length > 0 && (
        <div>
          <SectionHeading>Available Routes</SectionHeading>
          <div className="space-y-3">
            {data.routes.map((route, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 rounded-2xl bg-secondary/50 border border-border neuro-well"
              >
                <SignpostSplit className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <div>
                  <p className="font-montserrat font-bold text-sm text-foreground">
                    {route.name}
                    {route.status === "coming-soon" && (
                      <span className="ml-2 text-[10px] font-montserrat uppercase tracking-widest text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                        Coming Soon
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {route.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* VIP Options - Updated to match the provided design */}
      {data.vipOptions && data.vipOptions.length > 0 && (
        <div className="py-8">
          {/* Split Header */}
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-10">
            <h2 className="text-3xl md:text-4xl font-normal text-foreground tracking-tight">
              Why Upgrade to VIP?
            </h2>
            <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
              Gain a deeper understanding of the features offered by our VIP
              services, so we can provide transport tailored to your needs.
            </p>
          </div>

          {/* 2x2 Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.vipOptions.map((vip, i) => (
              <Card
                key={i}
                className="bg-card border border-border/50 rounded-sm p-8 shadow-sm flex flex-col"
              >
                {/* Icon Container (Dark Green Circle) */}
                <div className="h-12 w-12 rounded-full bg-[#1b4313] flex items-center justify-center mb-6 shrink-0">
                  <CarFront className="h-5 w-5 text-white" />
                </div>

                {/* Card Title */}
                <h3 className="text-lg font-medium text-foreground mb-3">
                  {vip.label}
                </h3>

                {/* Card Description */}
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                  {vip.description}
                </p>

                {/* Price (Separated at the bottom) */}
                <div className="mt-6 pt-4 border-t border-border/30">
                  <span className="font-bold text-foreground whitespace-nowrap">
                    +R{vip.price.toLocaleString()}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* FAQ */}
      {data.faq && data.faq.length > 0 && (
        <div>
          <SectionHeading>Frequently Asked Questions</SectionHeading>
          <Accordion type="single" collapsible className="space-y-2">
            {data.faq.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-secondary/50 border border-border rounded-2xl px-5 neuro-well"
              >
                <AccordionTrigger className="text-sm font-montserrat font-bold text-foreground hover:no-underline py-4">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground pb-4">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}

      {/* Cancellation Policy */}
      {data.cancellationPolicy && (
        <div className="p-6 rounded-2xl bg-secondary/50 border border-border neuro-well">
          <div className="flex items-start gap-3">
            <ExclamationTriangle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <SectionHeading>Cancellation Policy</SectionHeading>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {data.cancellationPolicy}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Gallery */}
      {data.gallery && data.gallery.length > 0 && (
        <div>
          <SectionHeading>Gallery</SectionHeading>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 snap-x snap-mandatory">
            {data.gallery.map((img, i) => (
              <div
                key={i}
                className="flex-none w-64 h-48 rounded-2xl overflow-hidden bg-secondary neuro-bezel snap-center"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);
